import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { CreateKnowledgeBaseCategoryDialog } from "@/components/feature/KnowledgebaseFilters/CreateKnowledgeBaseCategoryDialog"
import {
  type DocumentTagEntry,
  type DocumentTagMenuCategory,
  type DocumentTagMenuTag,
} from "@/components/feature/KnowledgebaseFilters/DocumentTagCategoryMenu"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { DEFAULT_CATEGORY_COLOR } from "@/constants/KnowledgeBaseConstant"
import {
  useCreateKnowledgeBaseTag,
  useGetKnowledgeBaseCategories,
  useSetBulkDocumentTags,
} from "@/hooks/useKnowledgeBaseCategories/useKnowledgeBaseCategories"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { brandColors } from "@/lib/brandColors"
import { KnowledgeBaseCategory } from "@/types/knowledgebaseCategories"
import { classNames } from "@/utils/classNames"
import { knowledgeBaseErrorKey } from "@/utils/knowledgeBaseErrors"
import { isLimitError } from "@/utils/limitErrors"
import { IconTag } from "@tabler/icons-react"
import { Plus } from "lucide-react"
import * as React from "react"
import { CategoryTagEditor, getLocalizedLabel } from "./TagDocumentShared"

interface BulkTagDocumentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  fileIds: string[]
  fileNames?: string[]
}

export const BulkTagDocumentDialog: React.FC<BulkTagDocumentDialogProps> = ({
  open,
  onOpenChange,
  fileIds,
}) => {
  const { currentLanguage, translate } = useTranslation()
  const [isCreateCategoryDialogOpen, setIsCreateCategoryDialogOpen] =
    React.useState(false)
  const [editingCategory, setEditingCategory] =
    React.useState<KnowledgeBaseCategory | null>(null)
  const [draftSelectedTagIdsByCategory, setDraftSelectedTagIdsByCategory] =
    React.useState<Record<string, string[]>>({})

  const { data: categories = [], isPending: isCategoriesPending } =
    useGetKnowledgeBaseCategories(false, open)
  const { mutateAsync: setBulkDocumentTags, isPending: isSaving } =
    useSetBulkDocumentTags()
  const { mutateAsync: createTag, isPending: isCreatingTag } =
    useCreateKnowledgeBaseTag()

  React.useEffect(() => {
    if (!open) {
      setDraftSelectedTagIdsByCategory({})
    }
  }, [open])

  React.useEffect(() => {
    if (!open) return
    setDraftSelectedTagIdsByCategory((current) => {
      let hasChanges = false
      const validTagIdsByCategory = new Map(
        categories.map((category) => [
          category.id,
          new Set(
            category.tags.filter((tag) => tag.isActive).map((tag) => tag.id),
          ),
        ]),
      )
      const nextSelections: Record<string, string[]> = {}
      for (const [categoryId, selectedTagIds] of Object.entries(current)) {
        const validTagIds = validTagIdsByCategory.get(categoryId)
        if (!validTagIds) {
          hasChanges = true
          continue
        }
        const filteredTagIds = selectedTagIds.filter((tagId) =>
          validTagIds.has(tagId),
        )
        if (filteredTagIds.length !== selectedTagIds.length) hasChanges = true
        nextSelections[categoryId] = filteredTagIds
      }
      return hasChanges ? nextSelections : current
    })
  }, [open, categories])

  const draftSelectedTags = React.useMemo<DocumentTagEntry[]>(() => {
    return categories.flatMap((category) => {
      const selectedTagIds = draftSelectedTagIdsByCategory[category.id] ?? []
      const tags = selectedTagIds
        .map((id) => category.tags.find((t) => t.id === id))
        .filter((tag): tag is KnowledgeBaseCategory["tags"][number] => !!tag)
        .map(
          (tag): DocumentTagMenuTag => ({
            id: tag.id,
            tagId: tag.id,
            tag,
            source: "user",
            approvedAt: null,
            categoryColor: category.color || DEFAULT_CATEGORY_COLOR,
          }),
        )
      const menuCategory: DocumentTagMenuCategory = {
        categoryId: category.id,
        categoryDisplayNameEn: getLocalizedLabel(
          category.displayNameEn,
          category.displayNameDe,
          currentLanguage,
        ),
        categoryColor: category.color || DEFAULT_CATEGORY_COLOR,
        tags,
      }
      return tags.map((tag) => ({ category: menuCategory, tag }))
    })
  }, [currentLanguage, draftSelectedTagIdsByCategory, categories])

  const visibleSelectedTagsByCategory = React.useMemo(() => {
    return draftSelectedTags.reduce<Record<string, DocumentTagEntry[]>>(
      (accumulator, entry) => {
        const existingEntries = accumulator[entry.category.categoryId] ?? []
        accumulator[entry.category.categoryId] = [...existingEntries, entry]
        return accumulator
      },
      {},
    )
  }, [draftSelectedTags])

  const hasPendingChanges = React.useMemo(() => {
    for (const draftTagIds of Object.values(draftSelectedTagIdsByCategory)) {
      if (draftTagIds.length > 0) return true
    }
    return false
  }, [draftSelectedTagIdsByCategory])

  const handleSelectionChange = React.useCallback(
    (categoryId: string, values: string[]) => {
      setDraftSelectedTagIdsByCategory((current) => ({
        ...current,
        [categoryId]: values,
      }))
    },
    [],
  )

  const handleCreateTag = React.useCallback(
    async (
      category: KnowledgeBaseCategory,
      tagName: string,
    ): Promise<string | undefined> => {
      if (!tagName.trim()) {
        toast.error(translate("KNOWLEDGEBASE/NAME_INVALID_ERROR"))
        return
      }
      try {
        const createdTag = await createTag({
          categoryId: category.id,
          data: { displayNameEn: tagName.trim() },
        })
        toast.success(
          translate("KNOWLEDGEBASE/TAG_CREATE_SUCCESS", { tag: tagName }),
        )
        return createdTag.id
      } catch (error) {
        if (!isLimitError(error)) {
          toast.error(
            translate(
              knowledgeBaseErrorKey(error, "KNOWLEDGEBASE/TAG_CREATE_ERROR"),
              { name: tagName },
            ),
          )
        }
      }
    },
    [createTag, translate],
  )

  const handleCategoryDialogOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setIsCreateCategoryDialogOpen(nextOpen)
      if (!nextOpen) setEditingCategory(null)
    },
    [],
  )

  const handleOpenCreateCategory = React.useCallback(() => {
    setEditingCategory(null)
    setIsCreateCategoryDialogOpen(true)
  }, [])

  const handleEditCategory = React.useCallback(
    (category: KnowledgeBaseCategory) => {
      setEditingCategory(category)
      setIsCreateCategoryDialogOpen(true)
    },
    [],
  )

  const handleCategoryDeleted = React.useCallback((categoryId: string) => {
    setDraftSelectedTagIdsByCategory((current) => {
      if (!(categoryId in current)) return current
      const nextSelections = { ...current }
      delete nextSelections[categoryId]
      return nextSelections
    })
  }, [])

  const handleSave = React.useCallback(async () => {
    const nextTagIds = Array.from(
      new Set(Object.values(draftSelectedTagIdsByCategory).flat()),
    )
    try {
      await setBulkDocumentTags({
        document_ids: fileIds,
        tag_ids: nextTagIds,
      })
      toast.success(translate("KNOWLEDGEBASE/BULK_TAG_SUCCESS"))
      onOpenChange(false)
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("KNOWLEDGEBASE/BULK_TAG_ERROR"))
      }
    }
  }, [
    draftSelectedTagIdsByCategory,
    fileIds,
    onOpenChange,
    setBulkDocumentTags,
    translate,
  ])

  const isLoading = isCategoriesPending

  return (
    <>
      <CreateKnowledgeBaseCategoryDialog
        open={isCreateCategoryDialogOpen}
        onOpenChange={handleCategoryDialogOpenChange}
        category={editingCategory}
        onCategoryDeleted={handleCategoryDeleted}
      />
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex flex-col bg-card p-0 sm:max-w-[500px]"
        >
          <SheetHeader className="border-b px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yellow1/10 text-yellow1">
                <IconTag size={20} stroke={1.75} aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <SheetTitle className="text-base">
                  {translate("KNOWLEDGEBASE/BULK_TAG_DIALOG_TITLE")}
                </SheetTitle>
                <SheetDescription className="mt-0.5 text-xs">
                  {translate("KNOWLEDGEBASE/BULK_TAG_DIALOG_DESCRIPTION", {
                    count: fileIds.length,
                  })}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-10 flex-1 overflow-y-auto px-6 py-2">
            {isLoading ? (
              <div className="flex min-h-40 items-center justify-center">
                <LoadingSpinner size={32} />
              </div>
            ) : (
              <section>
                <div className="mb-3 flex items-center justify-between">
                  <Typography
                    variant="textXS"
                    className="font-semibold tracking-wide text-muted-foreground uppercase"
                  >
                    {translate("KNOWLEDGEBASE/MANAGE_TAGS")}
                  </Typography>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 gap-1 px-2 text-xs"
                    onClick={handleOpenCreateCategory}
                  >
                    <Plus className="size-3" />
                    {translate("KNOWLEDGEBASE/CATEGORY_CREATE_TRIGGER")}
                  </Button>
                </div>
                {categories.length > 0 ? (
                  <div
                    className={classNames(
                      "space-y-5",
                      isSaving ? "opacity-70" : "",
                    )}
                  >
                    {categories.map((category) => (
                      <CategoryTagEditor
                        key={category.id}
                        category={category}
                        selectedEntries={
                          visibleSelectedTagsByCategory[category.id] ?? []
                        }
                        selectedTagIds={
                          draftSelectedTagIdsByCategory[category.id] ?? []
                        }
                        isCreatingTag={isCreatingTag}
                        onEditCategory={handleEditCategory}
                        onCreateTag={handleCreateTag}
                        onSelectionChange={(values) =>
                          handleSelectionChange(category.id, values)
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <Typography as="p" variant="textXS" className="text-grey5">
                    {translate(
                      "KNOWLEDGEBASE/FILE_TAG_DIALOG_EMPTY_CATEGORIES",
                    )}
                  </Typography>
                )}
              </section>
            )}
          </div>

          <SheetFooter className="border-t px-6 py-4">
            <div className="flex w-full items-center justify-end gap-2">
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                variant="outline"
                disabled={isSaving}
              >
                {translate("AI_CHAT/HISTORY/CANCEL")}
              </Button>
              <Button
                type="button"
                onClick={() => void handleSave()}
                disabled={!hasPendingChanges || isSaving}
              >
                {isSaving ? (
                  <LoadingSpinner
                    size={16}
                    className="mr-2"
                    color={brandColors.grey2}
                  />
                ) : null}
                {translate("AI_CHAT/HISTORY/SAVE")}
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  )
}
